// GOV-7 Increment E-2b — Business CRM Web fleet administration (vehicles + trackers).
// Consumes the SAME backend authority as Android/iOS Business:
//   - capability gating from GET /api/v1/me/service-workflow-eligibility
//       (vehicle_management_allowed / tracker_management_allowed / tracker_source_allowed)
//   - fleet CRUD from /api/v1/fleet/* (server-authoritative; tenant derived server-side).
// NO capability/role logic is re-derived here (backend 403s regardless). Names only — never raw ids as labels.
// Tracker credential is shown ONCE from the register/rotate response and lives only in component state
// (no localStorage/sessionStorage/IndexedDB/URL/console/analytics); cleared when the modal closes.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auth } from '../../../lib/firebase';
import { tenantHeaders } from '../../../lib/tenantSession';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function _getToken() {
  try { if (auth?.currentUser) return await auth.currentUser.getIdToken(); } catch (e) { /* fall through */ }
  return localStorage.getItem('crm_access_token') || '';
}

function _defaultApi() {
  // Fleet is TENANT-owned and none of its routes carries the tenant in the path, so the active
  // tenant must travel on the header. Without it the eligibility probe below failed closed and
  // the surface rendered "not available for your role or current plan" for a tenant that is
  // in fact entitled - a transport gap reported as an entitlement loss.
  const hdr = async (json) => ({ ...(json ? { 'Content-Type': 'application/json' } : {}), Authorization: `Bearer ${await _getToken()}`, ...tenantHeaders() });
  const wrap = async (r) => ({ ok: r.ok, status: r.status, data: r.ok || r.status < 500 ? await r.json().catch(() => ({})) : null });
  return {
    get: async (u) => wrap(await fetch(`${BACKEND_URL}${u}`, { headers: await hdr(false) })),
    post: async (u, b) => wrap(await fetch(`${BACKEND_URL}${u}`, { method: 'POST', headers: await hdr(true), body: JSON.stringify(b || {}) })),
    patch: async (u, b) => wrap(await fetch(`${BACKEND_URL}${u}`, { method: 'PATCH', headers: await hdr(true), body: JSON.stringify(b || {}) })),
  };
}

export default function FleetManager({ api: apiProp, eligibility: eligProp }) {
  const api = useMemo(() => apiProp || _defaultApi(), [apiProp]);
  const [elig, setElig] = useState(eligProp || null);
  const [eligLoaded, setEligLoaded] = useState(!!eligProp);
  const [vehicles, setVehicles] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [cap, setCap] = useState(null); // GOV-7 Issue A — { vehicles:{used,limit,limit_label}, trackers:{...} }
  const [err, setErr] = useState('');
  const [cred, setCred] = useState(null); // { credential, note } — one-time; NEVER persisted

  const canVehicles = !!elig?.vehicle_management_allowed;
  const canTrackers = !!elig?.tracker_management_allowed;

  const vehicleName = useCallback((id) => (vehicles.find((v) => v.vehicle_id === id) || {}).display_name || (id ? 'Unknown vehicle' : '—'), [vehicles]);

  const loadElig = useCallback(async () => {
    if (eligProp) { setElig(eligProp); setEligLoaded(true); return; }
    const r = await api.get('/api/v1/me/service-workflow-eligibility');
    setElig(r.data || null); setEligLoaded(true);
  }, [api, eligProp]);

  const loadFleet = useCallback(async () => {
    if (!elig?.vehicle_management_allowed) return;
    const v = await api.get('/api/v1/fleet/vehicles');
    if (v.ok) setVehicles(v.data?.vehicles || []);
    if (elig?.tracker_management_allowed) {
      const t = await api.get('/api/v1/fleet/trackers');
      if (t.ok) setTrackers(t.data?.sources || []);
    }
    // GOV-7 Issue A — Business-safe active-capacity meter (used vs allowed). Non-fatal if unavailable.
    const cp = await api.get('/api/v1/fleet/capacity');
    if (cp.ok) setCap(cp.data || null);
  }, [api, elig]);

  useEffect(() => { loadElig(); }, [loadElig]);
  useEffect(() => { if (eligLoaded) loadFleet(); }, [eligLoaded, loadFleet]);

  // Never render a raw object: a fleet-capacity 409 returns detail={code,...}; map the code to a safe plan
  // message, otherwise fall back to a string detail/message. (GOV-7 Issue A)
  const CAP_MSG = {
    vehicle_allowance_reached: 'Vehicle allowance reached for your current plan.',
    tracker_allowance_reached: 'Tracker allowance reached for your current plan.',
  };
  const safeErr = (r, fallback) => {
    const d = r?.data?.detail;
    const code = d && typeof d === 'object' ? d.code : null;
    setErr(CAP_MSG[code] || (typeof d === 'string' && d) || (d && typeof d === 'object' && d.message) || r?.data?.message || fallback);
  };
  // "N of M active" line — M null/absent => hide (—); -1/unlimited => "Unlimited".
  const capLine = (c) => {
    if (!c) return null;
    const lim = c.limit;
    if (lim == null) return `${c.used} active`;
    if (lim === -1 || c.limit_label === 'unlimited') return `${c.used} of Unlimited active`;
    return `${c.used} of ${lim} active`;
  };

  // ---- vehicle actions ----
  const [vName, setVName] = useState(''); const [vUnit, setVUnit] = useState(''); const [vType, setVType] = useState('');
  const createVehicle = async () => {
    setErr('');
    if (!vName.trim()) { setErr('Vehicle name is required.'); return; }
    const r = await api.post('/api/v1/fleet/vehicles', { display_name: vName.trim(), unit_number: vUnit.trim() || null, vehicle_type: vType.trim() || null });
    if (!r.ok) return safeErr(r, 'Could not create vehicle.');
    setVName(''); setVUnit(''); setVType(''); await loadFleet();
  };
  const deactivateVehicle = async (id) => { const r = await api.post(`/api/v1/fleet/vehicles/${encodeURIComponent(id)}/deactivate`); if (!r.ok) return safeErr(r, 'Could not deactivate.'); await loadFleet(); };

  // ---- vehicle full metadata edit (E-2c) ----
  // Enterprise CRUD over the EXISTING PATCH /api/v1/fleet/vehicles/{id} authority. Only backend-mutable
  // metadata is editable (display_name / unit_number / vehicle_type). Identity fields (vehicle_id / tenant_id /
  // created_at) and lifecycle status are NEVER sent from here — status changes go through deactivate. Editing is
  // an in-place update of the SAME canonical vehicle_id: no delete/recreate, so tracker bindings, the active job
  // assignment, ordered crew, and ping history all stay associated.
  const [editId, setEditId] = useState('');   // vehicle_id currently being edited ('' = none)
  const [eName, setEName] = useState(''); const [eUnit, setEUnit] = useState(''); const [eType, setEType] = useState('');
  const startEditVehicle = (v) => { setErr(''); setEditId(v.vehicle_id); setEName(v.display_name || ''); setEUnit(v.unit_number || ''); setEType(v.vehicle_type || ''); };
  const cancelEditVehicle = () => { setEditId(''); setEName(''); setEUnit(''); setEType(''); };
  const saveEditVehicle = async () => {
    setErr('');
    if (!eName.trim()) { setErr('Vehicle name is required.'); return; }
    // Send only safe mutable metadata against the canonical id — never identity/status.
    const r = await api.patch(`/api/v1/fleet/vehicles/${encodeURIComponent(editId)}`, {
      display_name: eName.trim(), unit_number: eUnit.trim() || null, vehicle_type: eType.trim() || null,
    });
    if (!r.ok) return safeErr(r, 'Could not update vehicle.');
    cancelEditVehicle(); await loadFleet();
  };

  // ---- tracker actions ----
  const [tName, setTName] = useState(''); const [tVeh, setTVeh] = useState('');
  const registerTracker = async () => {
    setErr('');
    if (!tName.trim()) { setErr('Tracker name is required.'); return; }
    const r = await api.post('/api/v1/fleet/trackers', { display_name: tName.trim(), vehicle_id: tVeh || null });
    if (!r.ok) return safeErr(r, 'Could not register tracker.');
    setTName(''); setTVeh('');
    if (r.data?.credential) setCred({ credential: r.data.credential, note: r.data.credential_note || 'Shown once — store it on the device now.' });
    await loadFleet();
  };
  const revokeTracker = async (id) => { const r = await api.post(`/api/v1/fleet/trackers/${encodeURIComponent(id)}/revoke`); if (!r.ok) return safeErr(r, 'Could not revoke.'); await loadFleet(); };
  const rotateTracker = async (id) => {
    const r = await api.post(`/api/v1/fleet/trackers/${encodeURIComponent(id)}/rotate`);
    if (!r.ok) return safeErr(r, 'Could not rotate credential.');
    if (r.data?.credential) setCred({ credential: r.data.credential, note: r.data.credential_note || 'Shown once — store it on the device now.' });
    await loadFleet();
  };
  const bindTracker = async (id, vehId) => { if (!vehId) return; const r = await api.post(`/api/v1/fleet/trackers/${encodeURIComponent(id)}/bind`, { vehicle_id: vehId }); if (!r.ok) return safeErr(r, 'Could not bind tracker.'); await loadFleet(); };

  if (!eligLoaded) return <div data-testid="fleet-loading">Loading…</div>;
  if (!canVehicles) return <div data-testid="fleet-unavailable">Fleet management is not available for your role or current plan.</div>;

  // shared control styling — spaced, independently clickable, accessible focus rings
  const inp = "border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 min-w-0";
  const btnPrimary = "px-3 py-2 text-sm font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-400 whitespace-nowrap";
  const btnGhost = "px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-sky-400 whitespace-nowrap";
  const btnDanger = "px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300 whitespace-nowrap";
  const StatusPill = ({ ok, on, off }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${ok ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>{ok ? on : off}</span>
  );

  return (
    <div data-testid="fleet-manager" className="space-y-8">
      {err ? <div data-testid="fleet-error" role="alert" className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{err}</div> : null}

      {/* one-time credential modal — state only, cleared on close */}
      {cred ? (
        <div data-testid="tracker-credential-modal" role="dialog" className="border border-amber-400 rounded-lg p-4 bg-amber-50">
          <p className="font-semibold text-amber-900">Tracker credential — shown once</p>
          <p className="text-sm text-amber-800">{cred.note}</p>
          <code data-testid="tracker-credential-value" className="block break-all my-2 bg-white border border-amber-200 rounded px-2 py-1 font-mono text-sm">{cred.credential}</code>
          <div className="flex gap-2">
            <button data-testid="tracker-credential-copy" type="button" className={btnGhost}
                    onClick={() => { try { navigator.clipboard?.writeText(cred.credential); } catch (e) { /* clipboard optional */ } }}>Copy</button>
            <button data-testid="tracker-credential-close" type="button" className={btnGhost} onClick={() => setCred(null)}>Close</button>
          </div>
        </div>
      ) : null}

      {/* vehicles */}
      <section data-testid="fleet-vehicles" className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Vehicles</h3>
          {capLine(cap?.vehicles) ? <span data-testid="fleet-vehicle-capacity" className="text-xs font-medium text-gray-500">{capLine(cap.vehicles)}</span> : null}
        </div>
        <div className="flex flex-wrap gap-2 items-center rounded-lg border border-gray-200 bg-gray-50 p-3">
          <input data-testid="vehicle-name" className={`${inp} flex-1`} placeholder="Display name (e.g. Truck 12)" value={vName} onChange={(e) => setVName(e.target.value)} />
          <input data-testid="vehicle-unit" className={`${inp} w-32`} placeholder="Unit # (optional)" value={vUnit} onChange={(e) => setVUnit(e.target.value)} />
          <input data-testid="vehicle-type" className={`${inp} w-36`} placeholder="Type (optional)" value={vType} onChange={(e) => setVType(e.target.value)} />
          <button data-testid="vehicle-create" type="button" className={btnPrimary} onClick={createVehicle}>Add vehicle</button>
        </div>
        {vehicles.length === 0 ? <div data-testid="vehicles-empty" className="text-sm text-gray-500 py-2">No vehicles yet.</div> : (
          <ul className="space-y-2">
            {vehicles.map((v) => (
              <li key={v.vehicle_id} data-testid="vehicle-row" className="rounded-lg border border-gray-200 bg-white p-3">
                {editId === v.vehicle_id ? (
                  <div data-testid="vehicle-edit-form" className="flex flex-wrap gap-2 items-center">
                    <input data-testid="vehicle-edit-name" className={`${inp} flex-1`} placeholder="Display name" value={eName} onChange={(e) => setEName(e.target.value)} />
                    <input data-testid="vehicle-edit-unit" className={`${inp} w-32`} placeholder="Unit # (optional)" value={eUnit} onChange={(e) => setEUnit(e.target.value)} />
                    <input data-testid="vehicle-edit-type" className={`${inp} w-36`} placeholder="Type (optional)" value={eType} onChange={(e) => setEType(e.target.value)} />
                    <button data-testid="vehicle-edit-save" type="button" className={btnPrimary} onClick={saveEditVehicle}>Save</button>
                    <button data-testid="vehicle-edit-cancel" type="button" className={btnGhost} onClick={cancelEditVehicle}>Cancel</button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span data-testid="vehicle-name-label" className="font-medium text-gray-900 truncate">{v.display_name}</span>
                        <StatusPill ok={v.status === 'active'} on="Active" off="Inactive" />
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        {v.unit_number ? <span>Unit {v.unit_number}</span> : null}
                        {v.unit_number && v.vehicle_type ? <span> · </span> : null}
                        {v.vehicle_type ? <span>{v.vehicle_type}</span> : null}
                        {!v.unit_number && !v.vehicle_type ? <span className="text-gray-400">No unit / type set</span> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid="vehicle-edit" type="button" className={btnGhost} onClick={() => startEditVehicle(v)}>Edit</button>
                      {v.status === 'active' ? <button data-testid="vehicle-deactivate" type="button" className={btnDanger} onClick={() => deactivateVehicle(v.vehicle_id)}>Deactivate</button> : null}
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* trackers — only when the assigned profile permits REGISTERED_VEHICLE_TRACKER */}
      <section data-testid="fleet-trackers" className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Vehicle trackers</h3>
          {canTrackers && capLine(cap?.trackers) ? <span data-testid="fleet-tracker-capacity" className="text-xs font-medium text-gray-500">{capLine(cap.trackers)}</span> : null}
        </div>
        {!canTrackers ? (
          <div data-testid="trackers-unavailable" className="text-sm text-gray-500 py-2">Vehicle trackers are not enabled for your current plan/profile.</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 items-center rounded-lg border border-gray-200 bg-gray-50 p-3">
              <input data-testid="tracker-name" className={`${inp} flex-1`} placeholder="Tracker name" value={tName} onChange={(e) => setTName(e.target.value)} />
              <select data-testid="tracker-vehicle" className={`${inp} w-44`} value={tVeh} onChange={(e) => setTVeh(e.target.value)}>
                <option value="">(no vehicle yet)</option>
                {vehicles.filter((v) => v.status === 'active').map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.display_name}</option>)}
              </select>
              <button data-testid="tracker-register" type="button" className={btnPrimary} onClick={registerTracker}>Register tracker</button>
            </div>
            {trackers.length === 0 ? <div data-testid="trackers-empty" className="text-sm text-gray-500 py-2">No trackers registered.</div> : (
              <ul className="space-y-2">
                {trackers.map((t) => (
                  <li key={t.source_id} data-testid="tracker-row" className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span data-testid="tracker-name-label" className="font-medium text-gray-900 truncate">{t.display_name}</span>
                          <StatusPill ok={t.status === 'active'} on="Active" off="Revoked" />
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5">
                          <span>Vehicle: {vehicleName(t.vehicle_id)}</span>
                          <span> · </span>
                          {t.last_seen_at ? <span data-testid="tracker-last-seen">last seen {t.last_seen_at}</span> : <span className="text-amber-600">no recent signal</span>}
                        </div>
                      </div>
                      {t.status === 'active' ? (
                        <div className="flex items-center gap-2 shrink-0 flex-wrap">
                          <select data-testid="tracker-bind" className={`${inp} w-44`} defaultValue={t.vehicle_id || ''} onChange={(e) => bindTracker(t.source_id, e.target.value)} aria-label="Assign vehicle">
                            <option value="">(assign vehicle…)</option>
                            {vehicles.filter((v) => v.status === 'active').map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.display_name}</option>)}
                          </select>
                          <button data-testid="tracker-rotate" type="button" className={btnGhost} onClick={() => rotateTracker(t.source_id)}>Rotate</button>
                          <button data-testid="tracker-revoke" type="button" className={btnDanger} onClick={() => revokeTracker(t.source_id)}>Revoke</button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>
    </div>
  );
}
